<template>
  <v-container class="app-container--width-max">
    <div class="d-flex justify-space-between">
      <h1 class="font-24 font-lg-28 font-weight-bold primary--text">
        <v-icon color="accent" large>mdi-file-find</v-icon>&nbsp;Fact find
      </h1>
    </div>

    <!-- the form -->
    <ValidationObserver ref="factFindForm">
      <form>
        <p class="mt-5 font-20 font-weight-bold primary--text">
          Current insurance arrangements
        </p>

        <BaseLabel
          text="Do you currently have an insurance policy in place?"
          class="mt-n2 mb-2"
        />
        <BaseToggleButton
          v-model="form.existingInsurancePolicy"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          :min-width="$vuetify.breakpoint.lgAndUp ? '110' : ''"
        />

        <!-- existing insurance policy -->
        <template v-if="form.existingInsurancePolicy">
          <BaseLabel
            text="When is your policy due to be renewed?"
            class="mt-10 mb-2"
          />
          <BaseInputDate
            ref="renewalDate"
            v-model="form.renewalDate"
            dataUiid=""
            hideDetails="auto"
            :rules="{
              required: {
                fieldLabel: 'Renewal date',
              },
              regex: {
                pattern: '^\\s*$|^(\\d{2})\\/(\\d{2})\\/\\d{4}$',
                errorMessageTemplate: 'Renewal date is invalid.',
              },
              date_valid: {
                errorMessageTemplate: 'Renewal date is invalid.',
              },
              date_in_range: {
                max: 1,
                units: 'years',
                fieldLabel: 'Renewal date',
              },
            }"
            :style="{
              'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
            }"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          />
          <BaseLabel
            text="Who are you currently insured with?"
            class="mt-10 mb-2"
          />
          <BaseInput
            v-model="form.insuranceProvider"
            hideDetails="auto"
            :rules="{
              required: {
                fieldLabel: 'Current insurer',
              },
            }"
            :style="{
              'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
            }"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            aria-label="Current insurer"
          />
          <BaseLabel
            text="What cover do you currently have?"
            class="mt-10 mb-2"
          />
          <BaseRadio
            v-model="form.coverType"
            pslStyle="image"
            dataUiid="CoverType"
            class="radio-cover-image"
            :items="setItemDataUiids('CoverType', HOME_COVER_TYPE_ITEMS)"
            :imageStyleBorderRadius="10"
            :rules="{
              required: {
                required: true,
                fieldLabel: 'Current cover',
              },
            }"
            :style="{
              'max-width': $vuetify.breakpoint.lgAndUp ? '700px' : '',
            }"
            aria-label="Current cover"
          />

          <!-- cover type buildings & contents -->
          <template v-if="isCoverTypeQuestionBuildings">
            <BaseLabel
              text="Current buildings cover amount?"
              class="mt-10 mb-2"
            />
            <BaseInput
              v-model="form.buildingsAmount"
              hideDetails="auto"
              prefix="£"
              :rules="{
                required: {
                  fieldLabel: 'Buildings amount',
                },
                regex: {
                  pattern: CURRENCY_REGEX,
                  fieldLabel: 'Buildings amount',
                },
                max_currency_length: {
                  length: 10,
                  fieldLabel: 'Please enter a valid currency amount',
                  errorMessageTemplate:
                    '{0} field may not be greater than 10 characters.',
                },
              }"
              :style="{
                'max-width': $vuetify.breakpoint.lgAndUp ? '254px' : '100%',
              }"
              :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
              validationMode="lazy"
              moneyFormat
              aria-label="Current buildings cover amount"
            />
          </template>

          <!-- cover type contents only -->
          <template v-if="isCoverTypeQuestionContents">
            <BaseLabel
              text="Current contents cover amount?"
              class="mt-10 mb-2"
            />
            <BaseInput
              v-model="form.contentsAmount"
              hideDetails="auto"
              prefix="£"
              :rules="{
                required: {
                  fieldLabel: 'Contents amount',
                },
                regex: {
                  pattern: CURRENCY_REGEX,
                  fieldLabel: 'Contents amount',
                },
                max_currency_length: {
                  length: 10,
                  fieldLabel: 'Please enter a valid currency amount',
                  errorMessageTemplate:
                    '{0} field may not be greater than 10 characters.',
                },
              }"
              :style="{
                'max-width': $vuetify.breakpoint.lgAndUp ? '254px' : '100%',
              }"
              :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
              validationMode="lazy"
              moneyFormat
              aria-label="Current contents cover amount"
            />
          </template>

          <BaseLabel
            text="Payment Frequency"
            class="mb-2"
            :class="
              isCoverTypeQuestionBuildings || isCoverTypeQuestionContents
                ? 'mt-10'
                : 'mt-11'
            "
          />
          <BaseToggleButton
            v-model="form.currentPaymentFrequency"
            :toggleButtonText="{
              yes: 'Monthly',
              yesDataUiid: '',
              no: 'Annually',
              noDataUiid: '',
            }"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            :min-width="$vuetify.breakpoint.lgAndUp ? '177' : ''"
          />

          <BaseLabel text="Current premium?" class="mt-10 mb-2" />
          <BaseInput
            v-model="form.currentPremium"
            hideDetails="auto"
            prefix="£"
            :rules="{
              required: {
                fieldLabel: 'Current premium',
              },
              regex: {
                pattern: CURRENCY_REGEX,
                fieldLabel: 'Current premium',
              },
              max_currency_length: {
                length: 8,
                fieldLabel: 'Please enter a valid currency amount',
                errorMessageTemplate:
                  '{0} field may not be greater than 8 characters.',
              },
            }"
            :style="{
              'max-width': $vuetify.breakpoint.lgAndUp ? '254px' : '100%',
            }"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            validationMode="lazy"
            moneyFormat
            aria-label="Current premium"
          />
        </template>

        <p class="mt-10 font-20 font-weight-bold primary--text">
          Demands and needs
        </p>

        <BaseLabel
          text="Is it important that you have cover for your buildings?"
          class="mt-n2 mb-2"
        />
        <BaseToggleButton
          v-model="form.buildingsCover"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          :min-width="$vuetify.breakpoint.lgAndUp ? '110' : ''"
        />

        <!-- AD buildings -->
        <template v-if="form.buildingsCover">
          <BaseLabel
            text="Is it important that you have accidental damage cover for buildings?"
            class="mt-10 mb-2"
          />
          <BaseToggleButton
            v-model="form.buildingsAccidentalCover"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            :min-width="$vuetify.breakpoint.lgAndUp ? '110' : ''"
          />
        </template>

        <BaseLabel
          text="Is it important that you have cover for your contents?"
          class="mt-10 mb-2"
        />
        <BaseToggleButton
          v-model="form.contentsCover"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          :min-width="$vuetify.breakpoint.lgAndUp ? '110' : ''"
        />

        <!-- AD contents -->
        <template v-if="form.contentsCover">
          <BaseLabel
            text="Is it important that you have accidental damage cover for contents?"
            class="mt-10 mb-2"
          />
          <BaseToggleButton
            v-model="form.contentsAccidentalCover"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            :min-width="$vuetify.breakpoint.lgAndUp ? '110' : ''"
          />
        </template>

        <template>
          <BaseLabel
            text="Is it important that you have cover for your personal belongings away from home?"
            class="mt-10 mb-2"
          />
          <BaseToggleButton
            v-model="form.coverPersonalBelongings"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            :min-width="$vuetify.breakpoint.lgAndUp ? '110' : ''"
          />
        </template>

        <BaseLabel
          text="Would you like cover for home emergencies?"
          class="mt-10 mb-2"
        />
        <BaseToggleButton
          v-model="form.coverHomeEmergencies"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          :min-width="$vuetify.breakpoint.lgAndUp ? '110' : ''"
        />
        <BaseLabel
          text="Would you like cover for legal expenses?"
          class="mt-10 mb-2"
        />
        <BaseToggleButton
          v-model="form.coverLegalExpenses"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          :min-width="$vuetify.breakpoint.lgAndUp ? '110' : ''"
        />

        <BaseLabel
          text="Is it important that you are able to choose the level of your policy excess?"
          class="mt-10 mb-2"
        />
        <BaseToggleButton
          v-model="form.policyExcess"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          :min-width="$vuetify.breakpoint.lgAndUp ? '110' : ''"
        />
        <BaseLabel
          text="Would you prefer to pay monthly or annually?"
          class="mt-10 mb-2"
        />
        <BaseToggleButton
          v-model="form.newPaymentFrequency"
          :toggleButtonText="{
            yes: 'Monthly',
            yesDataUiid: '',
            no: 'Annually',
            noDataUiid: '',
          }"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          :min-width="$vuetify.breakpoint.lgAndUp ? '177' : ''"
        />

        <p class="mt-10 font-20 font-weight-bold primary--text">
          Household income & expenditure
        </p>

        <BaseLabel text="Monthly net income" class="mt-n2 mb-2" />
        <BaseInput
          v-model="form.monthlyNetIncome"
          hideDetails="auto"
          prefix="£"
          :rules="{
            required: {
              fieldLabel: 'Monthly net income',
            },
            regex: {
              pattern: CURRENCY_REGEX,
              customMessage: 'Please enter a valid currency amount.',
            },
            max_currency_length: {
              length: 8,
              fieldLabel: 'Monthly net income',
              errorMessageTemplate:
                '{0} field may not be greater than 8 characters.',
            },
          }"
          :style="{
            'max-width': $vuetify.breakpoint.lgAndUp ? '254px' : '100%',
          }"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          validationMode="lazy"
          moneyFormat
          aria-label="Monthly net income"
        />
        <BaseLabel text="Mortgage &amp; home" class="mt-10 mb-2" />
        <BaseInput
          v-model="form.mortgageAndHome"
          hideDetails="auto"
          prefix="£"
          :rules="{
            required: {
              fieldLabel: 'Mortgage & home',
            },
            regex: {
              pattern: CURRENCY_REGEX,
              customMessage: 'Please enter a valid currency amount.',
            },
            max_currency_length: {
              length: 8,
              fieldLabel: 'Mortgage & home',
              errorMessageTemplate:
                '{0} field may not be greater than 8 characters.',
            },
          }"
          :style="{
            'max-width': $vuetify.breakpoint.lgAndUp ? '254px' : '100%',
          }"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          validationMode="lazy"
          moneyFormat
          aria-label="Mortgage and home"
        />
        <BaseLabel text="Lifestyle" class="mt-10 mb-2" />
        <BaseInput
          v-model="form.lifestyle"
          hideDetails="auto"
          prefix="£"
          :rules="{
            required: {
              fieldLabel: 'Lifestyle',
            },
            regex: {
              pattern: CURRENCY_REGEX,
              customMessage: 'Please enter a valid currency amount.',
            },
            max_currency_length: {
              length: 8,
              fieldLabel: 'Lifestyle',
              errorMessageTemplate:
                '{0} field may not be greater than 8 characters.',
            },
          }"
          :style="{
            'max-width': $vuetify.breakpoint.lgAndUp ? '254px' : '100%',
          }"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          validationMode="lazy"
          moneyFormat
          aria-label="Lifestyle"
        />
        <BaseLabel text="Travel" class="mt-10 mb-2" />
        <BaseInput
          v-model="form.travel"
          hideDetails="auto"
          prefix="£"
          :rules="{
            required: {
              fieldLabel: 'Travel',
            },
            regex: {
              pattern: CURRENCY_REGEX,
              customMessage: 'Please enter a valid currency amount.',
            },
            max_currency_length: {
              length: 8,
              fieldLabel: 'Travel',
              errorMessageTemplate:
                '{0} field may not be greater than 8 characters.',
            },
          }"
          :style="{
            'max-width': $vuetify.breakpoint.lgAndUp ? '254px' : '100%',
          }"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          validationMode="lazy"
          moneyFormat
          aria-label="Travel"
        />
        <BaseLabel text="Borrowing and debts" class="mt-10 mb-2" />
        <BaseInput
          v-model="form.borrowingAndDebts"
          hideDetails="auto"
          prefix="£"
          :rules="{
            required: {
              fieldLabel: 'Borrowing & debts',
            },
            regex: {
              pattern: CURRENCY_REGEX,
              customMessage: 'Please enter a valid currency amount.',
            },
            max_currency_length: {
              length: 8,
              fieldLabel: 'Borrowing & debts',
              errorMessageTemplate:
                '{0} field may not be greater than 8 characters.',
            },
          }"
          :style="{
            'max-width': $vuetify.breakpoint.lgAndUp ? '254px' : '100%',
          }"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          validationMode="lazy"
          moneyFormat
          aria-label="Borrowing and debts"
        />
        <BaseLabel text="Day to day expenses" class="mt-10 mb-2" />
        <BaseInput
          v-model="form.expenses"
          hideDetails="auto"
          prefix="£"
          :rules="{
            required: {
              fieldLabel: 'Day to day expenses',
            },
            regex: {
              pattern: CURRENCY_REGEX,
              customMessage: 'Please enter a valid currency amount.',
            },
            max_currency_length: {
              length: 8,
              fieldLabel: 'Day to day expenses',
              errorMessageTemplate:
                '{0} field may not be greater than 8 characters.',
            },
          }"
          :style="{
            'max-width': $vuetify.breakpoint.lgAndUp ? '254px' : '100%',
          }"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          moneyFormat
          aria-label="Day to day expenses"
        />
        <BaseLabel text="Total monthly expenditure" class="mt-10 mb-2" />
        <BaseInput
          hideDetails="auto"
          class="disabled-cursor"
          prefix="£"
          readonly
          hideValidationIcon
          :value="getTotalMonthlyExpenditure | formatDecimal"
          :style="{
            'max-width': $vuetify.breakpoint.lgAndUp ? '254px' : '100%',
          }"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          :rules="{
            max_currency_length: {
              length: 8,
              fieldLabel: 'Total monthly expenditure',
              errorMessageTemplate:
                '{0} field may not be greater than 8 characters.',
            },
          }"
          aria-label="Total monthly expenditure"
        />
        <BaseLabel text="Spendable income" class="mt-10 mb-2" />
        <BaseInput
          class="disabled-cursor"
          hideDetails="auto"
          prefix="£"
          readonly
          hideValidationIcon
          :value="getTotalSpendableIncome | formatDecimal"
          :style="{
            'max-width': $vuetify.breakpoint.lgAndUp ? '254px' : '100%',
          }"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          :rules="{
            max_currency_length: {
              length: 8,
              fieldLabel: 'Spendable income',
              errorMessageTemplate:
                '{0} field may not be greater than 8 characters.',
            },
          }"
          aria-label="Spendable income"
        />

        <p class="mt-8 font-20 font-weight-bold primary--text">
          Specific needs
        </p>

        <BaseLabel
          text="For the purposes of the quote, are there any specific insurance requirements you have?"
          class="mt-n2 mb-2"
          :style="{
            'max-width': $vuetify.breakpoint.lgAndUp ? '530px' : '100%',
          }"
        />
        <BaseTextarea
          v-model="form.specificNeeds"
          hideDetails="auto"
          hideValidationIcon
          :rules="{
            max: {
              length: 500,
              fieldLabel: 'Specific needs',
            },
          }"
          :style="{
            'max-width': $vuetify.breakpoint.lgAndUp ? '530px' : '100%',
          }"
          aria-label="Specific needs"
        />

        <BaseButton
          depressed
          rounded
          dense
          class="font-weight-bold font-20 pa-7 px-13 mt-10 mb-3"
          :color="'psorange'"
          :block="$vuetify.breakpoint.lgAndUp ? false : true"
          :text="'UPDATE FACT FIND'"
          @click="updateFactFind()"
        />
      </form>
    </ValidationObserver>
  </v-container>
</template>

<script>
import { EventBus } from "@/common/eventbus.js";
import { ValidationObserver } from "vee-validate";
import BaseLabel from "@/components/BaseComponents/BaseLabel";
import BaseInputDate from "@/components/BaseComponents/BaseInputDate";
import BaseButton from "@/components/BaseComponents/BaseButton";
import BaseInput from "@/components/BaseComponents/BaseInput";
import BaseRadio from "@/components/BaseComponents/BaseRadio";
import BaseTextarea from "@/components/BaseComponents/BaseTextarea";
import BaseToggleButton from "@/components/BaseComponents/BaseToggleButton";
import { mapState, mapActions, mapGetters } from "vuex";
import { QUESTIONS_MODULE } from "@/store/types/module-types";
import { FACT_FIND_FORM } from "@/constants/factfind.constants";
import { populateFactFindFromApi } from "@/common/factfind";
import {
  setItemDataUiids,
  HOME_COVER_TYPE_ITEMS,
} from "@/constants/component.constants.js";
import { ROUTE_NAME } from "@/constants/route.constants";
import { roundFloat, tryGoToNextStep } from "@/common/utils";
import { CURRENCY_REGEX } from "@/constants/regex-patterns.constants";
export default {
  name: "FactFindPage",
  components: {
    ValidationObserver,
    BaseLabel,
    BaseInputDate,
    BaseButton,
    BaseInput,
    BaseRadio,
    BaseTextarea,
    BaseToggleButton,
  },
  data() {
    return {
      setItemDataUiids,
      CURRENCY_REGEX,
      HOME_COVER_TYPE_ITEMS,
      form: FACT_FIND_FORM,
      currencyItems: [
        "buildingsAmount",
        "contentsAmount",
        "currentPremium",
        "monthlyNetIncome",
        "mortgageAndHome",
        "lifestyle",
        "travel",
        "borrowingAndDebts",
        "expenses",
      ],
    };
  },
  computed: {
    ...mapState(QUESTIONS_MODULE, ["quoteResponse", "restoringLastStep"]),
    ...mapGetters(QUESTIONS_MODULE, ["allSections"]),
    isCoverTypeQuestionBuildings() {
      return (
        this.form.coverType === "BuildingsAndContents" ||
        this.form.coverType === "BuildingsOnly"
      );
    },
    isCoverTypeQuestionContents() {
      return (
        this.form.coverType === "BuildingsAndContents" ||
        this.form.coverType === "ContentsOnly"
      );
    },
    getTotalMonthlyExpenditure() {
      const total =
        this.removeCharacters(this.form.mortgageAndHome) +
        this.removeCharacters(this.form.lifestyle) +
        this.removeCharacters(this.form.travel) +
        this.removeCharacters(this.form.borrowingAndDebts) +
        this.removeCharacters(this.form.expenses);
      return String(this.round(total));
    },
    getTotalSpendableIncome() {
      return String(
        this.round(
          Number(this.removeCharacters(this.form.monthlyNetIncome)) -
            Number(this.getTotalMonthlyExpenditure)
        )
      );
    },
  },
  watch: {
    "form.existingInsurancePolicy"(val) {
      if (!val) {
        this.form.renewalDate = "";
        this.form.insuranceProvider = "";
        this.form.coverType = "";
        this.$refs.renewalDate.resetFieldAll();
      }
    },
    "form.buildingsCover"(val) {
      if (!val) {
        this.form.buildingsAccidentalCover = false;
      }
    },
    "form.contentsCover"(val) {
      if (!val) {
        this.form.contentsAccidentalCover = false;
      }
    },
  },
  methods: {
    ...mapActions(QUESTIONS_MODULE, ["submitFactFind"]),

    removeCharacters(value) {
      const removeCharacters = value.replaceAll(/[,]/g, "");
      const stringToNum = Number(removeCharacters);
      return value.length ? stringToNum : 0;
    },

    async updateFactFind() {
      const isValid = await this.$refs.factFindForm.validate();
      if (!isValid) {
        EventBus.$emit("validation-error", "Validation error occurred");
        return;
      }
      this.currencyItems.forEach((key) => {
        this.form[key] = this.form[key].replace(/,/g, "");
      });
      this.submitFactFind({
        ...this.form,
        totalMonthlyExpenditure: this.getTotalMonthlyExpenditure,
        totalSpendableIncome: Number(this.getTotalSpendableIncome),
      });

      this.$router.push({ name: ROUTE_NAME.HOME.B2B.COVER_TYPE });
    },
    round(number) {
      return roundFloat(number);
    },
  },
  mounted() {
    if (this.quoteResponse?.Answers)
      populateFactFindFromApi(this.quoteResponse.Answers, this.form);

    if (!this.restoringLastStep) {
      return;
    }

    setTimeout(async () => {
      tryGoToNextStep(
        this.$refs.factFindForm,
        async (form) => await form.validate(),
        ROUTE_NAME.HOME.B2B.COVER_TYPE
      );
    }, 0);
  },
};
</script>

<style lang="scss" scoped>
.disabled-cursor :deep(.v-input__slot),
.disabled-cursor :deep(.v-input__slot *) {
  cursor: not-allowed !important;
}
</style>
